import { DateTime } from 'luxon';
import { PrintRow } from '~Components/PrinterManager';
import { i18nProps } from '~Types';
import { AggregatedCategoryMap } from '~utilities';

export type ReportRow = {
  leftText: string;
  rightText: string;
  isHeader?: boolean;
  bold?: boolean;
};

export const getAggregatedCategoryRows = (
  categories: AggregatedCategoryMap,
  formatCurrency: (value: number) => string,
  i18n: (key: string) => string,
  level = 0,
): ReportRow[] => {
  let rows: ReportRow[] = [];

  Object.entries(categories).forEach(([categoryId, category]) => {
    // Skip categories with count 0
    if (category.count === 0) return;

    const hasSubCategories = category.subCategory && Object.keys(category.subCategory).length > 0;

    const directSubCategoryExists =
      category.subCategory &&
      Object.entries(category.subCategory).some(
        ([, subCategory]) =>
          subCategory.name?.endsWith(`(${i18n('Report.AggregatedCategories.Direct')})`) &&
          subCategory.count === category.count,
      );

    const spaceBefore = level === 0 ? '' : '  '.repeat(level);

    // Add the current category as a row
    rows.push({
      leftText: `${spaceBefore}${category.count}x ${category.name}`,
      rightText: formatCurrency(category.amount),
      bold: level === 0,
    });

    // Add subcategories unless they're "direct" and equal in count
    if (!directSubCategoryExists && hasSubCategories) {
      const subRows = getAggregatedCategoryRows(
        category.subCategory,
        formatCurrency,
        i18n,
        level + 1,
      );

      rows = [...rows, ...subRows];
    }
  });

  return rows;
};

const TOTAL_ROW_LENGTH = 40;

export const formatRow = (content: string, rightContent?: string): PrintRow[] => {
  const rightContentFormatted = rightContent ? rightContent : '';
  const totalLength = TOTAL_ROW_LENGTH;
  const paddingNeeded = totalLength - content.length - rightContentFormatted.length;

  if (paddingNeeded < 0) {
    return [
      {
        content:
          content.slice(0, totalLength - rightContentFormatted.length - 3) +
          '...' +
          rightContentFormatted,
        alignment: 'CENTER',
      },
    ];
  }

  return [
    {
      content: content + ' '.repeat(paddingNeeded) + rightContentFormatted,
      alignment: 'CENTER',
    },
  ];
};

export const getSubtitleRow = (content: string): PrintRow[] => {
  if (content.length === 0) {
    return [
      {
        content: '-'.repeat(TOTAL_ROW_LENGTH),
        alignment: 'CENTER',
      },
    ];
  }

  const paddingNeeded = TOTAL_ROW_LENGTH - content.length - 2; // Adjust for 1 space on each side
  const leftPadding = Math.floor(paddingNeeded / 2);
  const rightPadding = paddingNeeded - leftPadding;

  return [
    {
      content: ' ',
    },
    {
      content: '-'.repeat(leftPadding) + ' ' + content + ' ' + '-'.repeat(rightPadding),
      alignment: 'CENTER',
    },
    {
      content: ' ',
    },
  ];
};

export const printReport = (
  title: string,
  rows: ReportRow[],
  i18n: i18nProps,
  subTitle?: string,
) => {
  const titleObject: PrintRow = {
    content: title,
    fontSize: 28,
    bold: true,
    alignment: 'CENTER',
  };

  const subTitleObject: PrintRow | undefined = subTitle
    ? {
        content: subTitle,
        bold: true,
        alignment: 'CENTER',
      }
    : undefined;

  const data: PrintRow[] = [
    titleObject,
    {
      content: ' ',
    },
    ...(subTitleObject ? [subTitleObject] : []),
    {
      content: i18n('Report.PrintedAt') + ' ' + DateTime.now().toFormat('yyyy-MM-dd HH:mm'),
      bold: true,
      alignment: 'CENTER',
    },
    ...rows.flatMap((row) => {
      return row.isHeader ? getSubtitleRow(row.leftText) : formatRow(row.leftText, row.rightText);
    }),
    {
      content: ' ',
    },
    {
      content: ' ',
    },
  ];

  window.printerManager?.initiatePrint({
    printType: 'REPORT',
    data: data,
  });
};
